import { useEffect, useState, useCallback } from 'react'
import Joyride from 'react-joyride'
import { useLocation } from 'react-router-dom'

const TOUR_STORAGE_KEY = 'kinstak:tours:galleryJoyrideCompleted'
const AUTO_START_MAX_WAIT_MS = 8000
const AUTO_START_POLL_MS = 200

const GalleryJoyride = () => {
  const [run, setRun] = useState(false)
  const [steps, setSteps] = useState([])

  const location = useLocation()

  const buildSteps = useCallback(() => [
    {
      target: '#nav-search-form',
      title: 'Search',
      content: 'Search your assets by keywords.',
      placement: 'bottom',
      disableBeacon: true
    },
    {
      target: '.view-files-dropdown',
      title: 'View Gallery',
      content:
        'Here is where you can see all of your assets. You can also view items categorized by Duplicates and Favorites.',
      placement: 'bottom',
      disableBeacon: true
    },
    {
      target: '.create-category-dropdown',
      title: 'Create a category',
      content:
        'Create custom categories like Weddings, Birthdays, Trips, and more to organize your assets.',
      placement: 'bottom',
      disableBeacon: true
    },
    {
      target: '#upload-button',
      title: 'Upload',
      content: 'Upload more assets from your device or cloud providers using this button.',
      placement: 'bottom',
      disableBeacon: true
    },
    {
      target: '[data-super-title*="Event"] .row-title',
      title: 'Events & Locations',
      content: 'These rows show assets auto-categorized by event, holiday, or location.',
      placement: 'right',
      disableBeacon: true
    },
    {
      target: '[data-super-title*="Kin"] .row-title',
      title: 'Kin categories',
      content: 'Here are your custom categories where you grouped assets (Birthday, Wedding, etc.).',
      placement: 'right',
      disableBeacon: true
    },
    {
      target: '[data-super-title="Years"] .row-title',
      title: 'Years',
      content: 'Click a year to view assets grouped by that year.',
      placement: 'right',
      disableBeacon: true
    },
    {
      target: '[data-super-title="Documents"] .row-title',
      title: 'Documents',
      content:
        'The Documents row shows your uploaded documents. Click a document to open it or view by year.',
      placement: 'right',
      disableBeacon: true
    }
  ], [])

  const startTour = useCallback(() => {
    const isCompleted = window.localStorage.getItem(TOUR_STORAGE_KEY) === 'true'
    if (isCompleted) {
      return
    }

    const s = buildSteps()
    setSteps(s)
    setRun(true)
  }, [buildSteps])

  const waitForTargetsAndStart = useCallback(
    (reason = 'auto') => {
      const isCompleted =
        window.localStorage.getItem(TOUR_STORAGE_KEY) === 'true'
      if (isCompleted) {
        return
      }

      const startedAt = Date.now()

      const requiredSelectors = [
        '#nav-search-form',
        '.view-files-dropdown',
        '.create-category-dropdown',
        '#upload-button'
      ]

      const hasRequiredTargets = () =>
        requiredSelectors.every(sel => Boolean(document.querySelector(sel)))

      const tick = () => {
        if (hasRequiredTargets()) {
          startTour()
          return
        }

        if (Date.now() - startedAt > AUTO_START_MAX_WAIT_MS) {
          return
        }

        window.setTimeout(tick, AUTO_START_POLL_MS)
      }

      tick()
    },
    [startTour]
  )

  useEffect(() => {
    if (!location.pathname.startsWith('/gallery')) return

    waitForTargetsAndStart('route')

    const onLoad = () => waitForTargetsAndStart('window-load')
    window.addEventListener('load', onLoad)

    return () => window.removeEventListener('load', onLoad)
  }, [location.pathname, waitForTargetsAndStart])

  useEffect(() => {
    const startHandler = () => {
      waitForTargetsAndStart('event')
    }

    window.addEventListener('start-gallery-tour', startHandler)
    return () => window.removeEventListener('start-gallery-tour', startHandler)
  }, [waitForTargetsAndStart])

  const handleCallback = data => {
    const status = data.status
    if (status === 'finished' || status === 'skipped') {
      window.localStorage.setItem(TOUR_STORAGE_KEY, 'true')
      setRun(false)
    }
  }

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous={true}
      showSkipButton={true}
      disableBeacon={true}
      scrollToFirstStep={true}
      disableScrolling={false}
      scrollOffset={120}
      scrollDuration={250}
      styles={{ options: { zIndex: 10000 } }}
      callback={handleCallback}
    />
  )
}

export default GalleryJoyride
